import { useState } from "react";

interface ChannelConfig {
  kakaoChannelId: string;
  kakaoChannelUrl: string;
  lineId: string;
  lineUrl: string;
}

const defaultConfig: ChannelConfig = {
  kakaoChannelId: "",
  kakaoChannelUrl: "",
  lineId: "",
  lineUrl: "",
};

export default function ChannelSettingsTab() {
  const [config, setConfig] = useState<ChannelConfig>(defaultConfig);
  const [saved, setSaved] = useState<ChannelConfig>(defaultConfig);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleSave = () => {
    setSaved({ ...config });
    showToast("채널 설정이 저장됐어요!");
  };

  const hasChanges = JSON.stringify(config) !== JSON.stringify(saved);

  return (
    <div className="max-w-2xl">
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-stone-800 text-white text-sm px-4 py-3 rounded-lg">
          {toast}
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-bold text-stone-800">채널 설정</h2>
        <p className="text-sm text-stone-400 mt-1">카카오톡 채널과 라인 ID를 연동해 버튼을 활성화하세요.</p>
      </div>

      {/* 카카오톡 섹션 */}
      <div className="bg-white rounded-xl border border-stone-100 p-6 mb-4">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-yellow-400 flex items-center justify-center">
            <i className="ri-kakao-talk-fill text-stone-800 text-lg"></i>
          </div>
          <div>
            <h3 className="font-semibold text-stone-800">카카오톡 채널</h3>
            <p className="text-xs text-stone-400">카카오톡 채널 ID와 링크를 입력하세요.</p>
          </div>
          {saved.kakaoChannelId && (
            <span className="ml-auto px-2.5 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">
              연동 완료
            </span>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">채널 ID</label>
            <div className="flex items-center gap-0">
              <span className="px-3 py-2.5 bg-stone-50 border border-r-0 border-stone-200 rounded-l-lg text-sm text-stone-400">@</span>
              <input
                type="text"
                className="flex-1 px-3 py-2.5 border border-stone-200 rounded-r-lg text-sm focus:outline-none focus:border-yellow-400 text-stone-700"
                placeholder="your-channel-id"
                value={config.kakaoChannelId}
                onChange={(e) => setConfig({ ...config, kakaoChannelId: e.target.value })}
              />
            </div>
            <p className="text-xs text-stone-400 mt-1">카카오 비즈니스 채널 아이디 (@ 제외)</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">채널 링크 URL</label>
            <input
              type="text"
              className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-yellow-400 text-stone-700"
              placeholder="https://pf.kakao.com/_xxxxx"
              value={config.kakaoChannelUrl}
              onChange={(e) => setConfig({ ...config, kakaoChannelUrl: e.target.value })}
            />
            <p className="text-xs text-stone-400 mt-1">카카오 채널 URL (버튼 클릭 시 연결되는 주소)</p>
          </div>
        </div>

        <div className="mt-5 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
          <p className="text-xs text-yellow-700 font-medium mb-1">
            <i className="ri-information-line mr-1"></i>카카오 채널 만들기
          </p>
          <p className="text-xs text-yellow-600">
            카카오 비즈니스(<a href="https://business.kakao.com" target="_blank" rel="noopener noreferrer" className="underline">business.kakao.com</a>)에서 채널을 개설한 후 채널 홈 URL을 복사해 붙여넣으세요.
          </p>
        </div>
      </div>

      {/* 라인 섹션 */}
      <div className="bg-white rounded-xl border border-stone-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
            <i className="ri-line-fill text-white text-lg"></i>
          </div>
          <div>
            <h3 className="font-semibold text-stone-800">라인 (LINE)</h3>
            <p className="text-xs text-stone-400">라인 ID와 링크를 입력하세요.</p>
          </div>
          {saved.lineId && (
            <span className="ml-auto px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full font-medium">
              연동 완료
            </span>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">라인 ID</label>
            <input
              type="text"
              className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400 text-stone-700"
              placeholder="your-line-id"
              value={config.lineId}
              onChange={(e) => setConfig({ ...config, lineId: e.target.value })}
            />
            <p className="text-xs text-stone-400 mt-1">라인 앱 → 설정 → 프로필에서 확인 가능</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">라인 링크 URL</label>
            <input
              type="text"
              className="w-full px-3 py-2.5 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-emerald-400 text-stone-700"
              placeholder="https://line.me/ti/p/~your-line-id"
              value={config.lineUrl}
              onChange={(e) => setConfig({ ...config, lineUrl: e.target.value })}
            />
            <p className="text-xs text-stone-400 mt-1">line.me/ti/p/~{"{라인ID}"} 형식으로 입력</p>
          </div>
        </div>

        <div className="mt-5 p-3 bg-emerald-50 rounded-lg border border-emerald-100">
          <p className="text-xs text-emerald-700 font-medium mb-1">
            <i className="ri-information-line mr-1"></i>라인 링크 생성 방법
          </p>
          <p className="text-xs text-emerald-600">
            라인 ID가 "fukuoka_stay"라면 URL은 https://line.me/ti/p/~fukuoka_stay 입니다. LINE 공식 계정의 경우 <a href="https://manager.line.biz" target="_blank" rel="noopener noreferrer" className="underline">LINE Official Account Manager</a>에서 확인하세요.
          </p>
        </div>
      </div>

      {/* 미리보기 */}
      {(config.kakaoChannelUrl || config.lineUrl) && (
        <div className="bg-white rounded-xl border border-stone-100 p-5 mb-6">
          <h4 className="text-sm font-semibold text-stone-700 mb-3">버튼 미리보기</h4>
          <div className="flex gap-3">
            {config.kakaoChannelUrl && (
              <a
                href={config.kakaoChannelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-yellow-400 text-stone-800 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-yellow-300"
              >
                <i className="ri-kakao-talk-fill text-base"></i>카카오톡 문의
              </a>
            )}
            {config.lineUrl && (
              <a
                href={config.lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-emerald-600"
              >
                <i className="ri-line-fill text-base"></i>라인 문의
              </a>
            )}
          </div>
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={!hasChanges}
        className={`px-8 py-3 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-all ${
          hasChanges
            ? "bg-stone-800 text-white hover:bg-stone-700"
            : "bg-stone-100 text-stone-400 cursor-not-allowed"
        }`}
      >
        <i className="ri-save-line mr-2"></i>설정 저장
      </button>
    </div>
  );
}
